import React, { memo, useCallback } from 'react';
import type { StepNode } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { createPickerChangeEvent } from '../../../utils/eventEmitter';

import { CopyBlock } from '../../shared/CopyBlock';
import { Media } from '../../shared/Media';
import { Picker } from '../../shared/Picker';
import { ActionBar } from '../../shared/ActionBar';
import { Background } from '../../shared/Background';
import { HeaderBar } from '../../shared/HeaderBar';
import styles from './WheelPicker.module.css';

export interface WheelPickerProps {
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
}

export const WheelPicker = memo(function WheelPicker({
  step,
  locale,
  variables,
  resolvedScreenValues: _resolvedScreenValues,
  onAction,
  onEvent,
  editMode = false,
  previewMode,
}: WheelPickerProps): React.ReactElement {
  const { content } = step;

  const handlePickerChange = useCallback((index: number, value: string) => {
    if (onEvent) {
      onEvent(createPickerChangeEvent(value, step, `picker-item-${index}`));
    }
  }, [step, onEvent]);

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;
  const mediaHeightPercentage = content?.featuredMedia?.appearance?.dimensions?.heightPercentage;
  const hasStaticMediaHeight = typeof mediaHeightPercentage === 'number' && mediaHeightPercentage > 0;
  const isMediaTopAligned = content?.featuredMedia?.appearance?.topAlignment === 'top' && !hasStaticMediaHeight;

  return (
    <div
      className={`${styles.container}${previewMode === 'live' ? ` ${styles.liveMode}` : ''}${isMediaTopAligned ? ` ${styles.noPaddingTop}` : ''}${editMode ? ' screen-view--edit-mode' : ''}`}
      data-testid="view-wheel-picker"
    >

      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      <HeaderBar
        header={content?.header}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.header` : undefined}
        absolute={isMediaTopAligned}
      />

      <div className={`${styles.content}${isMediaTopAligned ? ` ${styles.contentTopAligned}` : ''}`}>
        {content?.featuredMedia && (
          <Media
            media={content.featuredMedia}
            locale={locale}
            editPath={ep ? `${ep}.featuredMedia` : undefined}
            fillContainer={isMediaTopAligned}
            previewMode={previewMode}
          />
        )}

        <CopyBlock
          copyBlock={content?.copy}
          locale={locale}
          variables={variables}
          editPath={ep ? `${ep}.copy` : undefined}
          className={styles.xPaddings}
        />

        <div className={`${styles.pickerContainer} ${styles.xPaddings}`}>
          <Picker
            roller={content?.roller}
            locale={locale}
            onIndexChange={handlePickerChange}
            editPath={ep ? `${ep}.roller` : undefined}
            previewMode={previewMode}
          />
        </div>
      </div>

      <ActionBar
        actionBar={content?.actionBar}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.actionBar` : undefined}
      />
    </div>
  );
});

WheelPicker.displayName = 'WheelPicker';
